import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import CommandPalette from '@/components/CommandPalette';
import {
  LayoutDashboard, Inbox, Columns, Users, Settings,
  Zap, ChevronDown, Moon, Sun, LogOut, Menu, X, Bell,
  FileText, Calendar, UserPlus, Sparkles, Search as SearchIcon,
  Sun as TodayIcon, BookOpen, Gift, Globe, ScrollText, BarChart3, Mic, FolderOpen, Video,
  CalendarCheck as CalendarIcon, User as UserIcon,
} from 'lucide-react';

const NAV_ITEMS = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/today', label: 'Today', icon: TodayIcon },
  { to: '/admin/inbox', label: 'Inbox', icon: Inbox },
  { to: '/admin/leads', label: 'Leads', icon: UserPlus },
  { to: '/admin/pipeline', label: 'Pipeline', icon: Columns },
  { to: '/admin/clients', label: 'Clients', icon: Users },
  { to: '/admin/wizards', label: 'Wizards', icon: Sparkles },
  { to: '/admin/forms', label: 'Forms', icon: FileText },
  { to: '/admin/documents', label: 'Documents', icon: FolderOpen },
  { to: '/admin/calendar', label: 'Calendar', icon: Calendar },
  { to: '/admin/delivery', label: 'Delivery', icon: CalendarIcon },
  { to: '/admin/zoom', label: 'Zoom Calls', icon: Video },
  { to: '/admin/kb', label: 'Knowledge', icon: BookOpen },
  { to: '/admin/meetings', label: 'Meetings (Notes)', icon: Mic },
  { to: '/admin/reports', label: 'Reports & NPS', icon: BarChart3 },
  { to: '/admin/referrals', label: 'Referrals', icon: Gift },
  { to: '/admin/minisites', label: 'Mini-sites', icon: Globe },
  { to: '/admin/audit', label: 'Audit Log', icon: ScrollText },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
];

export default function OperatorLayout() {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Global ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'OP';

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--bg)', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
      {/* Sidebar */}
      <aside
        className={`sidebar fixed inset-y-0 left-0 z-50 w-60 flex flex-col transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:relative lg:flex`}
        data-testid="operator-sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-4 border-b" style={{ borderColor: '#273033' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#006080' }}>
            <Zap size={16} color="white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-bold text-sm leading-tight">{branding.app_name}</div>
            <div className="text-xs" style={{ color: '#5C6B72' }}>Operator</div>
          </div>
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
            style={{ color: '#8A99A0' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? 'active' : ''}`
              }
            >
              <Icon size={16} strokeWidth={2} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Bottom user area */}
        <div className="px-3 py-3 border-t" style={{ borderColor: '#273033' }}>
          <button
            className="flex items-center gap-2.5 w-full p-2 rounded-lg transition-colors hover:bg-white/5"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            data-testid="user-menu-button"
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
              style={{ background: '#006080' }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-xs font-semibold text-white truncate">{user?.name || 'Operator'}</div>
              <div className="text-xs truncate" style={{ color: '#5C6B72' }}>{user?.email}</div>
            </div>
            <ChevronDown size={14} style={{ color: '#5C6B72' }} />
          </button>

          {userMenuOpen && (
            <div
              className="mt-2 rounded-btn overflow-hidden border"
              style={{ background: '#1A2428', borderColor: '#273033' }}
              data-testid="user-menu-dropdown"
            >
              <button
                className="flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors hover:bg-white/5"
                onClick={() => { setUserMenuOpen(false); navigate('/admin/profile'); }}
                data-testid="profile-link"
                style={{ color: '#8A99A0' }}
              >
                <UserIcon size={14} />
                Profile &amp; password
              </button>
              <button
                className="flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors hover:bg-white/5"
                onClick={() => setDark(!dark)}
                data-testid="toggle-theme"
                style={{ color: '#8A99A0' }}
              >
                {dark ? <Sun size={14} /> : <Moon size={14} />}
                {dark ? 'Light mode' : 'Dark mode'}
              </button>
              <button
                className="flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors hover:bg-white/5"
                onClick={handleLogout}
                data-testid="logout-button"
                style={{ color: '#8A99A0' }}
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar (mobile + desktop header) */}
        <header
          className="h-12 flex items-center justify-between px-4 border-b flex-shrink-0"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
            style={{ color: 'var(--muted)' }}
          >
            <Menu size={20} />
          </button>
          <div className="hidden lg:flex flex-1 max-w-md">
            <button
              onClick={() => setPaletteOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-btn w-full transition-colors"
              style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--muted)' }}
              data-testid="global-search-button"
            >
              <SearchIcon size={13} />
              <span>Search projects, clients, bugs, docs…</span>
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--border)' }}>⌘K</span>
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              className="w-8 h-8 flex items-center justify-center rounded-btn transition-colors"
              style={{ color: 'var(--muted)' }}
              data-testid="notifications-button"
            >
              <Bell size={16} />
            </button>
            <button
              className="w-8 h-8 flex items-center justify-center rounded-btn transition-colors"
              onClick={() => setDark(!dark)}
              style={{ color: 'var(--muted)' }}
              data-testid="header-theme-toggle"
            >
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
